import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const liveAlertsRulesGuide: AdminGuide = {
  id: "live-alerts-rules",
  title: "Use Live Alerts rules and source badges",
  category: "Customer Support",
  audience: ["Support", "Ops", "CS", "Finance"],
  summary:
    "Understand why an alert is in the queue, where the source record lives, and when to suppress, close, or archive it.",
  keywords: [
    "live alerts",
    "source badge",
    "suppressed",
    "closed",
    "monday",
    "stripe",
    "issue report",
  ],
  location: "Admin",
  risk: "medium",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.LIVE_ALERT_FEED,
    GUIDE_SOURCE_DOCS.LIVE_ALERT_RULES,
    GUIDE_SOURCE_DOCS.LOGGING,
  ],
  whatThisHelpsYouDo: [
    "Read the source badge before opening a record.",
    "Use the status lifecycle without hiding work by mistake.",
    "Explain Monday, issue-report, and billing alerts from the rule catalog.",
  ],
  whenToUse: [
    "You are reviewing `/live-alerts` and need to know why a row appears.",
    "You need to suppress an alert with a reason and expiry.",
    "You need to tell whether a source record is read-only from Live Alerts.",
  ],
  whenNotToUse: [
    "You need to change the source record itself. Open the source system instead.",
    "You need to share internal delivery details, logs, or tokens. Do not share those.",
  ],
  beforeYouStart: [
    "Open `/live-alerts`.",
    "Check the source badge and source-record link before changing status.",
  ],
  steps: [
    "Read the row title and summary to confirm the staff action.",
    "Use the source badge to confirm where the record lives.",
    "Open the source record when the next step needs the authoritative system.",
    "Use `acknowledged` when someone has seen or claimed the alert but work is not done.",
    "Use `suppressed` only with a clear reason and expiry.",
    "Use `closed` when there is no more operational work.",
    "Use `archive` only for the separate staff-hide action.",
    "Open the **Rules** popup from `/live-alerts?rules=business` when you need the full lifecycle and source-boundary summary.",
  ],
  warnings: [
    "Monday alerts are read-only in Live Alerts. Change Monday status, comments, and owners in Monday.",
    "Suppression hides the row from the default queue, so the reason and expiry must be specific.",
    "Archive is not the normal lifecycle end state.",
  ],
  checklists: [
    {
      title: "Before suppressing",
      items: [
        "The source record is not ready for action right now.",
        "The reason says what staff are waiting for.",
        "The expiry is within ten business days.",
      ],
    },
  ],
  whatSuccessLooksLike: [
    "Staff can tell the source system before clicking.",
    "Suppressed alerts are still visible through the Suppressed status filter.",
    "Closed alerts remain searchable and auditable.",
  ],
  troubleshooting: [
    {
      title: "A Monday item is done but the alert still appears",
      body: [
        "Check whether the Monday status label is one of the mapped done/closed labels.",
        "If it is mapped, the alert resolves first and closes after the two-business-day soak.",
        "It should not auto-archive.",
      ],
    },
    {
      title: "A source badge looks wrong",
      body: [
        "Treat the badge map in `docs/admin/readmes/LIVE_ALERT_RULES.md` as canonical.",
        "Escalate if the API returns a badge inferred from provider text.",
      ],
    },
  ],
  escalateWhen: [
    "A source link opens an unexpected host.",
    "A suppressed alert has no reason or expiry.",
    "A billing alert for failed Stripe webhooks, failed transactions, or open disputes is missing.",
  ],
  relatedAdminPaths: [
    { label: "Live Alerts", path: "/live-alerts" },
    { label: "Live Alerts rules", path: "/live-alerts?rules=business" },
  ],
};
